import {useEffect, useState} from 'react';
import {useTheme, Badge, Skeleton, Typography, Tooltip} from '@mui/material';
import Avatar from '@jobseeker/lotus/components/Avatar';

import {Box} from '@jobseeker/lotus/foundations';
import {ButtonV2, Button, VersionVariants} from '@jobseeker/lotus/elements';
import {useIsCustomWidth, useIsLargeDevice} from '@jobseeker/lotus/hooks';

import Header from '@jobseeker/lotus/components/Header';
import Search from '@jobseeker/feature-navigation/components/Search';

import Nav from '@jobseeker/lotus/components/Nav';
import Menu from '@jobseeker/lotus/components/Menu/Menu';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  HamburgerMenu,
  Notification,
  Message,
  Profile,
  ProfileOrange,
  NotificationOld,
  WhiteCrossOutlined,
} from '@jobseeker/lotus/assets/icons';

import {getOldLink} from '@jobseeker/lotus/utils';
import Drop from '@jobseeker/lotus/components/Drop/Drop';
import BottomSheet from '@jobseeker/lotus/elements/BottomSheet';
import ProfileDrawer from '@jobseeker/lotus/components/ProfileDrawer';

import type {HeaderNav as Props} from '@jobseeker/lotus/components/Header/types';
import NotificationList from '@jobseeker/feature-navigation/components/NotificationList';
import {useInfoBannerHeight} from '@jobseeker/common-core';
import {
  getUbaPageName,
  handleTracking,
  openUrl,
  TrackEventUBA,
  deferTask,
} from '@jobseeker/common-core/utils';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import AvatarWithProgress from '@jobseeker/lotus/components/AvatarWithProgress';
import ProfileDrop from '@jobseeker/lotus/components/ProfileDrop';
import {loadIimjobsCategoriesTabs} from '@jobseeker/common-core/utils/categories';

import {TooltipStyled} from '@jobseeker/lotus/elements/Tooltip';
import {MdAutoAwesome} from 'react-icons/md';
import {shallowEqual, useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import type {MenuItemType} from '@jobseeker/lotus/components/Menu/types';
import {getAppDownloadLinkagesUrls} from '@jobseeker/common-core/utils/common';

const HeaderWithNav = (props: Props) => {
  const {
    OnSearchBtnClick,
    OnLogoutBtnClick,
    tabsList = [],
    courses = [{}],
    others = [],
    isLoggedIn = false,
    config: {showNavMenu = false, showCourses = false},
    profileDrawerOptions,
    notification,
    courseLink,
    selectedNav,
    layoutValidated,
    subscriptionMeta,
    reCap,
    page,
    savedJobCount,
    showPremium,
    chatCount,
    handleMenuLogin,
    readNotification,
    aiSearchActive,
  } = props || {};

  const {
    name,
    email,
    picUrl,
    phoneVerified,
    percentage,
    proMembership,
    followUpsRemaining,
    subscriptions,
    showSpotLight,
  } = props?.profileInfo || {
    name: '',
    email: '',
    picUrl: '',
    phoneVerified: false,
    proMembership: {
      isValid: false,
      daysLeft: 0,
    },
    followUpsRemaining: 0,
  };

  const metadata: any = useInterceptor();
  const {downloadLinks} = metadata;

  const theme = useTheme();
  const isLargeDevice = useIsLargeDevice();
  const bannerHeight = useInfoBannerHeight();

  const [open, setOpen] = useState(false);
  const [openDrop, setDropOpen] = useState(false);
  const [loginBtnActive, setLoginBtnActive] = useState(false);
  const [otherMenu, setOtherMenu] = useState<Array<MenuItemType>>([]);

  const [anchorEl, setAnchorEl] = useState(null);

  const [openDrawer, setDrawerOpen] = useState(false);
  const [showBadgeOnNotification, setShowBadgeOnNotification] = useState(true);
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const isSmall = useIsCustomWidth(500);
  const {profile} = useSelector((state: AppState) => state.user, shallowEqual);
  const categoryId = useSelector(
    (state: AppState) => state.app.context.navigation.actualCategory
  );
  const tagId = useSelector(
    (state: AppState) => state.app.context.navigation.tag,
    shallowEqual
  );

  const featureFlags = useSelector(
    (state: AppState) => state.app.featureFlags,
    shallowEqual
  );

  const handleMenuClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleMenuOpen = (event: React.BaseSyntheticEvent) => {
    const {currentTarget} = event;
    setOpen(true);
    setAnchorEl(currentTarget);
  };

  const handleDropClose = () => {
    deferTask(() => {
      setDropOpen(false);
      setAnchorEl(null);
    });
  };

  const handleDropOpen = (event: React.BaseSyntheticEvent) => {
    if (!isAppIimjobs) {
      handleDrawer(false);
      const [ref, origin] = (function getRefTagAndOrigin() {
        if (window?.location) {
          const urlParams = new URLSearchParams(window.location.search);
          return [urlParams?.get('ref') ?? '', window.location.pathname];
        }
        return ['', ''];
      })();

      handleTracking('jsClickNotification', {
        event_category: 'JobListing',
        Source: origin,
        UserId: sessionStorage.getItem('userId') || 'NA',
        Device: sessionStorage.getItem('device') || 'desktop',
        Ref: ref,
      });
    }
    const {currentTarget} = event;
    setDropOpen(true);
    setAnchorEl(currentTarget);
    setShowBadgeOnNotification(false);
  };

  const handleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };

  const sendEvent = (action: string) => {
    handleTracking(`Use_app_icon_${action}`, {
      event_category: 'App_promotion',
      Origin: page,
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
      Php_SessionID_Mod: sessionStorage.getItem('phpSessId') || '0',
    });
  };

  useEffect(() => {
    if (isSmall) sendEvent('View');
  }, [isSmall]);

  const notifications = notification?.list || [];

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const [transTabsList, setTransTabsList] = useState<any[]>(tabsList || []);

  useEffect(() => {
    if (!isAppIimjobs) return;
    (async () => {
      try {
        const transformed = await loadIimjobsCategoriesTabs();
        if (Array.isArray(transformed) && transformed.length > 0) {
          setTransTabsList(transformed);
        }
      } catch {}
    })();
  }, [isAppIimjobs]);

  useEffect(() => {
    if (others.length > 0) {
      if (isAppIimjobs) {
        setOtherMenu(others);
      } else {
        const filteredOthers = others.map(item => {
          if (item.label === 'Login as Jobseeker') {
            return {
              ...item,
              handleClick: () => {
                handleMenuLogin && handleMenuLogin();
                const trackingData = {
                  pageName: getUbaPageName(),
                  sessionUsername: sessionStorage.getItem('userId') || '',
                };
                TrackEventUBA('loginPageView', 'view', trackingData);
              },
            };
          } else {
            return item;
          }
        });
        setOtherMenu([...filteredOthers]);
      }
    }
  }, [others]);

  const handleUbaTracking = (action = 'click') => {
    const trackingData = {
      pageName: getUbaPageName(),
      linkageName: 'Top Menu Use App Button',
      linkageType: 'useAppButton',
      sessionUsername: sessionStorage.getItem('userId') || '',
      ...(action === 'click' && {typeClick: 'CTA'}),
    };
    TrackEventUBA(
      action === 'click' ? 'appDownloadLinkageClick' : 'appDownloadLinkageView',
      action,
      trackingData
    );
  };

  const onTooltipCloseClick = () => {
    setTooltipOpen(false);

    const now = Date.now();
    const tooltipTimeStamp = localStorage.getItem(
      'ai-search-tooltip-shown-timestamp'
    );
    const is24Hours =
      tooltipTimeStamp && now - Number(tooltipTimeStamp) < 24 * 60 * 60 * 1000;
    if (!tooltipTimeStamp || !is24Hours) {
      localStorage.setItem('ai-search-tooltip-shown-timestamp', String(now));
    }

    TrackEventUBA('topMenuClick', 'click', {
      categoryID: page === 'category' ? categoryId : undefined,
      tagID: page === 'tag' ? Number(tagId) : undefined,
      page,
      sessionUsername: sessionStorage.getItem('userId') || '',
      clickedMenuName: 'closeAIToolTip',
    });
  };

  useEffect(() => {
    const shownTimestamp = localStorage.getItem(
      'ai-search-tooltip-shown-timestamp'
    );
    const shown = localStorage.getItem('ai-search-tooltip-shown');
    const now = Date.now();

    let shouldShow = false;

    if ((!shown || !shownTimestamp) && aiSearchActive) {
      shouldShow = true;
    }

    if (
      shown &&
      shownTimestamp &&
      now - Number(shownTimestamp) > 24 * 60 * 60 * 1000 &&
      aiSearchActive
    ) {
      shouldShow = true;
    }

    if (shouldShow) {
      setTooltipOpen(true);
      localStorage.setItem('ai-search-tooltip-shown', 'true');
    } else {
      setTooltipOpen(false);
    }
  }, [aiSearchActive]);

  return (
    <Header proMeta={subscriptionMeta?.mobileNavBar} {...props}>
      {!props.showExploreJobs && showNavMenu && (
        <Nav
          tabsList={transTabsList}
          courses={courses}
          showCourses={showCourses}
          courseLink={courseLink}
          showPremium={showPremium}
          selectedNav={selectedNav}
          toShowSelect={props?.showMenuSelect}
        />
      )}
      {layoutValidated && (
        <>
          <Box
            display="flex"
            marginLeft="auto"
            alignItems="center"
            onMouseLeave={handleDropClose}
          >
            {isLoggedIn && !props?.showExploreJobs && (
              <Box
                display={isAppIimjobs ? 'flex' : {xxs: 'flex', md: 'none'}}
                width={{xxs: '29px', sm: '33px'}}
                height={'100%'}
                justifyContent={'center'}
                data-testid="chat_icon"
                sx={{
                  color: 'secondary.main',
                  marginRight: theme.spacing(1.125),
                  ':hover': isAppIimjobs
                    ? {
                        background: 'white',
                        boxShadow: '6px 6px 12px 2px rgba(41, 41, 41, 0.04)',
                        borderRadius: '20px',
                        border: '0.10px rgba(126, 126, 126, 0.40) solid',
                      }
                    : {},
                  cursor: 'pointer',
                }}
                onClick={event => {
                  event?.preventDefault();
                  openUrl(getOldLink('/chats'), '_self');
                }}
              >
                <Image
                  src={
                    isAppIimjobs
                      ? Message
                      : 'data:image/png;base64,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'
                  }
                  alt={'Chat'}
                  width={isAppIimjobs ? (isLargeDevice ? 18 : 20) : 23}
                  height={isAppIimjobs ? (isLargeDevice ? 18 : 20) : 23}
                  objectFit="cover"
                  quality={50}
                  onClick={event => {
                    event?.preventDefault();
                    openUrl(getOldLink('/chats'), '_self');
                  }}
                  style={{
                    margin: theme.spacing(0.875),
                  }}
                />
              </Box>
            )}

            {!props?.showExploreJobs && !isAppIimjobs && (
              <Search
                theme={theme}
                isLargeDevice={isLargeDevice}
                onClick={OnSearchBtnClick}
                isCustomeWidth={useIsCustomWidth}
                calledFrom=""
                handleDropClose={handleDropClose}
                hideHover={!isAppIimjobs}
              />
            )}

            {!isLoggedIn && (
              <Box
                display="flex"
                gap={1}
                flexWrap="nowrap"
                alignItems="center"
                marginRight={isAppIimjobs ? theme.spacing(1.125) : 0}
              >
                {isAppIimjobs && (
                  <ButtonV2
                    sx={{
                      '@media (width:1280px)': {
                        width: '95px',
                      },
                      width: {md: '100px', lg: '125px'},
                      background: '#000',
                      px: 2,
                      py: 1,
                      height: 'fit-content',
                      backgroundColor: theme.palette.secondary.main,
                    }}
                    variant="contained"
                    size="large"
                    onClick={() => {
                      let toUrl = '/#signup';
                      if (window?.location) {
                        const {pathname, search} = window.location;
                        const urlParams = new URLSearchParams(search);
                        const ref = urlParams?.get('ref');

                        if (page === 'course' || page === 'women_at_work') {
                          const currentUrl = `${pathname}${search || ''}`;
                          const encodedRedirect =
                            encodeURIComponent(currentUrl);
                          const query =
                            ref && ref.length
                              ? `redirectto=${encodedRedirect}&ref=${encodeURIComponent(
                                  ref
                                )}`
                              : `redirectto=${encodedRedirect}`;
                          toUrl = `/?${query}#signup`;
                        } else {
                          if (ref && ref.length) {
                            toUrl = `/?ref=${ref}#signup`;
                          }
                        }
                      }
                      openUrl(toUrl, '_self');
                    }}
                    onMouseEnter={handleMenuClose}
                  >
                    Register
                  </ButtonV2>
                )}
                {!isAppIimjobs && !isLoggedIn && (
                  <Box display={{xxs: 'none', md: 'flex'}}>
                    <Button
                      variant="outlined"
                      label="explore_jobs"
                      size="small"
                      data-testid="explore_jobs"
                      sx={{
                        width: 110,
                        fontSize: '15px',
                        border: '1px solid',
                        lineHeight: '16px',
                        py: 1,
                        px: 0,
                        marginLeft: 1,
                        fontWeight: 700,
                        ':hover': {
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                        },
                      }}
                      onMouseEnter={event => {
                        setLoginBtnActive(true);
                        handleMenuOpen(event);
                      }}
                      onMouseLeave={() => {
                        setLoginBtnActive(false);
                      }}
                    >
                      <Image
                        src={loginBtnActive ? Profile : ProfileOrange}
                        alt={'right'}
                        width={16}
                        height={16}
                        style={{
                          verticalAlign: 'middle',
                          marginRight: '10px',
                        }}
                      />
                      Login
                    </Button>
                    {otherMenu.length > 0 && (
                      <Menu
                        value={0}
                        open={open}
                        anchorEl={anchorEl}
                        menuItems={otherMenu}
                        handleMenuClose={handleMenuClose}
                        theme={theme}
                        indexTab={0}
                        width={'180px'}
                      />
                    )}
                  </Box>
                )}
              </Box>
            )}

            {!props?.showExploreJobs && isAppIimjobs && (
              <TooltipStyled
                isGrey={isAppIimjobs ? false : true}
                sx={{zIndex: 1600}}
                title={
                  <Box
                    display={'flex'}
                    width={'auto'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    gap={1}
                  >
                    <MdAutoAwesome color="#fff" width={6} height={6} />
                    <Typography
                      fontWeight={500}
                      fontSize={isAppIimjobs ? '11px' : 14}
                    >
                      Try AI Search
                    </Typography>
                    <Image
                      style={{cursor: 'pointer'}}
                      onClick={e => {
                        e.stopPropagation();
                        deferTask(() => onTooltipCloseClick());
                      }}
                      src={WhiteCrossOutlined}
                      width={12}
                      height={12}
                      alt=""
                    />
                  </Box>
                }
                onClose={() => setTooltipOpen(false)}
                open={featureFlags?.enableAISearch && tooltipOpen}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                placement="top-start"
                arrow
                background={!isAppIimjobs && '#676765'}
                PopperProps={{
                  disablePortal: true,
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -6],
                      },
                    },
                    {
                      name: 'eventListeners',
                      options: {
                        scroll: false,
                        resize: false,
                      },
                    },
                  ],
                }}
              >
                <div>
                  <Search
                    theme={theme}
                    isLargeDevice={isLargeDevice}
                    onClick={(event: React.MouseEvent) => {
                      onTooltipCloseClick();
                      OnSearchBtnClick?.(event);
                    }}
                    isCustomeWidth={useIsCustomWidth}
                    calledFrom="nav"
                    handleDropClose={handleDropClose}
                  />
                </div>
              </TooltipStyled>
            )}

            {isLoggedIn && notification && !props?.showExploreJobs && (
              <>
                <Box
                  display="flex"
                  alignItems="center"
                  width={{xxs: '24px', sm: '33px'}}
                  height={'100%'}
                  justifyContent={'center'}
                  data-testid="notification_icon"
                  sx={{
                    color: 'secondary.main',
                    marginRight: isAppIimjobs ? 1.125 : {xxs: 0, md: 2},
                    marginLeft: {xxs: isAppIimjobs ? 0 : 1.125, md: 0},
                    ':hover': isAppIimjobs
                      ? {
                          background: 'white',
                          boxShadow: '6px 6px 12px 2px rgba(41, 41, 41, 0.04)',
                          borderRadius: '20px',
                          border: '0.10px rgba(126, 126, 126, 0.40) solid',
                        }
                      : {},
                    cursor: 'pointer',
                  }}
                  onMouseEnter={handleDropOpen}
                >
                  <Badge
                    overlap="circular"
                    color="error"
                    badgeContent=" "
                    invisible={
                      notification?.count > 0 && showBadgeOnNotification
                        ? false
                        : true
                    }
                    sx={{
                      '& .MuiBadge-badge': {
                        color: theme.palette.error.dark,
                        width: theme.spacing(1),
                        height: theme.spacing(1),
                        padding: 0,
                        minWidth: theme.spacing(1),
                        top: '36%',
                        right: '36%',
                      },
                    }}
                  >
                    <Image
                      src={isAppIimjobs ? Notification : NotificationOld}
                      alt={'Notification'}
                      width={isAppIimjobs ? (isLargeDevice ? 18 : 20) : 22}
                      height={isAppIimjobs ? (isLargeDevice ? 18 : 20) : 22}
                      objectFit="cover"
                      quality={50}
                      onClick={() => {}}
                      style={{
                        margin: theme.spacing(0.875),
                      }}
                    />
                  </Badge>
                </Box>
                {notifications.length >= 0 &&
                  (isLargeDevice ? (
                    <Drop
                      open={openDrop}
                      bannerHeight={bannerHeight}
                      anchorEl={anchorEl}
                      handleMenuClose={handleDropClose}
                      theme={theme}
                      title={'Notifications'}
                      isForMapper={isAppIimjobs ? false : true}
                      children={
                        <NotificationList
                          notificationList={notifications.slice(0, 5)}
                          forwardLink={getOldLink('/allnotification')}
                          theme={theme}
                          onMenuClose={handleDropClose}
                          markNotificationRead={readNotification}
                        />
                      }
                      count={isAppIimjobs ? notification?.count : 0}
                      popperSx={
                        isAppIimjobs
                          ? {}
                          : {
                              position: 'fixed !important',
                              marginTop: '-50px !important',
                              transform:
                                'translate3d(-88px, 50px, 0px) !important',
                              paddingRight: '0px !important',
                              '@media (min-width: 1720px)': {
                                right: '146px !important',
                                paddingTop: '16px !important',
                              },
                            }
                      }
                    />
                  ) : (
                    <BottomSheet
                      open={openDrop}
                      title={'Notifications'}
                      onDismiss={handleDropClose}
                      children={
                        <NotificationList
                          notificationList={notifications}
                          forwardLink={
                            isAppIimjobs ? '' : getOldLink('/allnotification')
                          }
                          theme={theme}
                          onMenuClose={handleDropClose}
                          markNotificationRead={readNotification}
                        />
                      }
                    ></BottomSheet>
                  ))}
              </>
            )}
            {isLoggedIn && isSmall && !props?.showExploreJobs && (
              <Box display="flex" flexWrap="nowrap" alignItems="center">
                <Button
                  variant="outlined"
                  label="use_app"
                  size="small"
                  data-testid="use_app"
                  sx={{
                    fontSize: !isAppIimjobs ? {xs: '11px', sm: '12px'} : '12px',
                    borderRadius: theme.spacing(5),
                    px: 1.375,
                    py: 0.625,
                    fontWeight: isAppIimjobs ? 500 : 700,
                    backgroundColor: isAppIimjobs ? 'transparent' : '#FFF3EF',
                    marginLeft: isAppIimjobs ? '0px' : {xs: '5px', sm: '10px'},
                  }}
                  onClick={() => {
                    deferTask(() => {
                      sendEvent('Click');
                      const storeUrl = isAppIimjobs
                        ? downloadLinks.useApp.url
                        : getAppDownloadLinkagesUrls(5);
                      !isAppIimjobs && handleUbaTracking('click');
                      openUrl(storeUrl, '_blank');
                    });
                  }}
                >
                  USE APP
                </Button>
              </Box>
            )}
          </Box>
          {props?.showExploreJobs && (
            <Box mr={{xxs: 0, lg: 3.5}}>
              <Button
                variant={
                  isAppIimjobs ? 'outlined' : VersionVariants.VERSION_ONE
                }
                label="explore_jobs"
                size="small"
                data-testid="explore_jobs"
                sx={{
                  width: 140,
                  fontSize: '14px',
                  border: '1px solid',
                  borderRadius: isAppIimjobs ? theme.spacing(8) : 'default',
                  color: isAppIimjobs
                    ? 'primary.main'
                    : theme.palette.primary.main,
                  px: 3,
                  py: 1.375,
                  lineHeight: '16px',
                }}
                onClick={() => {
                  openUrl('/jobfeed', '_self');
                }}
              >
                Explore Jobs
              </Button>
            </Box>
          )}
          <Box
            display={{xxs: 'none', md: isLoggedIn ? 'contents' : 'inline-flex'}}
            onMouseLeave={handleMenuClose}
          >
            {isLoggedIn && (
              <>
                {!isAppIimjobs && (
                  <>
                    <Box
                      sx={{position: 'relative'}}
                      onClick={() => {
                        handleDrawer(true);
                      }}
                      data-testid="header-profile-avatar-container"
                    >
                      <AvatarWithProgress
                        profilePicture={picUrl}
                        percentage={percentage ?? 0}
                        isOnline={chatCount && chatCount > 0 ? true : false}
                        data-testid="header-profile-avatar"
                      />

                      <Drop
                        open={openDrawer}
                        bannerHeight={bannerHeight}
                        anchorEl={anchorEl}
                        handleMenuClose={() => handleDrawer(false)}
                        theme={theme}
                        title={''}
                        isForMapper={isAppIimjobs ? false : true}
                        data-testid="header-profile-drop"
                        children={
                          <ProfileDrop
                            theme={theme}
                            name={name}
                            email={email}
                            profileProgress={percentage ?? 0}
                            profilePicUrl={picUrl ?? ''}
                            isOnline={chatCount && chatCount > 0 ? true : false}
                            proMembership={proMembership}
                            followUps={followUpsRemaining}
                            OnLogoutBtnClick={OnLogoutBtnClick}
                            profileDrawerOptions={profileDrawerOptions}
                            proMeta={subscriptionMeta?.desktopNavBar}
                            reCap={reCap}
                            savedJobCount={savedJobCount}
                            showPremium={showPremium}
                            chatCount={chatCount}
                            subscriptions={subscriptions}
                            showSpotLight={showSpotLight}
                            page={page}
                          />
                        }
                        count={0}
                      />
                    </Box>
                  </>
                )}
                {isAppIimjobs && (
                  <>
                    {!!email ? (
                      <Box
                        display={{xxs: 'none', lg: 'flex'}}
                        sx={{
                          cursor: 'pointer',
                          marginLeft: theme.spacing(1.195),
                        }}
                        onClick={() => {
                          handleDrawer(true);
                        }}
                      >
                        <Avatar
                          showAnimation={true}
                          isOnline={false}
                          imgUrl={picUrl}
                          sx={{
                            borderRadius: theme.spacing(0.625),
                            width: theme.spacing(4.26),
                            height: theme.spacing(4.26),
                            borderColor: '#91B87929',
                            color: theme.palette.primary.light,
                          }}
                        />
                      </Box>
                    ) : (
                      <Skeleton
                        variant={isAppIimjobs ? 'rounded' : 'circular'}
                        width={theme.spacing(4.26)}
                        height={theme.spacing(4.26)}
                        sx={{
                          ml: theme.spacing(1.195),
                          display: {xxs: 'none', md: 'block'},
                        }}
                      />
                    )}
                    {email && (
                      <ProfileDrawer
                        theme={theme}
                        handleDrawer={handleDrawer}
                        openDrawer={openDrawer}
                        name={name}
                        email={email}
                        phoneVerified={phoneVerified}
                        profileProgress={percentage}
                        profilePicUrl={picUrl}
                        proMembership={proMembership}
                        followUps={followUpsRemaining}
                        OnLogoutBtnClick={OnLogoutBtnClick}
                        profileDrawerOptions={profileDrawerOptions}
                        aboutSection={props?.others || []}
                        proMeta={subscriptionMeta?.desktopNavBar}
                        reCap={reCap}
                        savedJobCount={savedJobCount}
                        page={page}
                      />
                    )}
                  </>
                )}
              </>
            )}

            {!isLoggedIn && isAppIimjobs && (
              <>
                <Box
                  color="secondary"
                  sx={{
                    display: {xxs: 'none', lg: 'flex'},
                    fontWeight: theme.typography.fontWeightBold,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                      textDecorationThickness: 4,
                      textDecorationColor: 'primary',
                    },
                  }}
                  onMouseEnter={handleMenuOpen}
                >
                  <Image
                    src={HamburgerMenu}
                    alt={'Search'}
                    data-name="navbar-search"
                    width={16}
                    height={16}
                    objectFit="cover"
                    quality={50}
                  />
                </Box>
                {others.length > 0 && (
                  <Menu
                    value={0}
                    open={open}
                    anchorEl={anchorEl}
                    menuItems={others}
                    handleMenuClose={handleMenuClose}
                    theme={theme}
                    indexTab={0}
                  />
                )}
              </>
            )}
          </Box>{' '}
        </>
      )}
    </Header>
  );
};

export default HeaderWithNav;
